// EditTransferCaseModal.jsx - Fully Fixed & Clean
import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Html5QrcodeScanner } from 'html5-qrcode';
import axiosInstance from '../../../../../api';

const EditTransferCaseModal = ({ show, onHide, transferCaseData, refreshList }) => {
  const title = 'Edit Transfer Case';
  const caseId = transferCaseData?.id;

  // States
  const [medicalCenters, setMedicalCenters] = useState([]);
  const [transferFrom, setTransferFrom] = useState('');
  const [transferTo, setTransferTo] = useState('');
  const [filteredTransferTo, setFilteredTransferTo] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [fileInputs, setFileInputs] = useState([0]);
  const [isLoading, setIsLoading] = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  const [formData, setFormData] = useState({
    id: '',
    name: '',
    diagnose: '',
    bracelet_id: '',
    doctor: '',
    doctor_name: '',
    age: '',
    gender: '',
    documents: {},
    clinic_no: '',
    request_details: '',
    escort_team: {
      escort_physician: '',
      escort_nurse: '',
      escort_paramedic: '',
      other: ''
    }
  });

  const [errors, setErrors] = useState({});

  const MAX_FILES = 3;

  // Fetch full case data when modal opens
  useEffect(() => {
    if (!show || !caseId) return;

    const fetchCaseData = async () => {
      setIsLoading(true);
      try {
        const authToken = sessionStorage.getItem('auth_token');
        const response = await axiosInstance.get(`/ems/cases/${caseId}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        const data = response.data.data || response.data;

        const ps = data.patient_situation || {};

        setFormData({
          id: data.id || '',
          name: data.case_name || '',
          diagnose: data.diagnose || '',
          bracelet_id: data.bracelet_id || '',
          doctor: data.doctor?.id || '',
          doctor_name: data.doctor_name || '',
          age: data.age || '',
          gender: data.gender || '',
          documents: {}, // new uploads only

          // These come from patient_situation
          clinic_no: ps.clinic_no || '',
          request_details: ps.request_details || '',
          escort_team: {
            escort_physician: ps.escort_team?.escort_physician || '',
            escort_nurse: ps.escort_team?.escort_nurse || '',
            escort_paramedic: ps.escort_team?.escort_paramedic || '',
            other: ps.escort_team?.other || ''
          }
        });

        setTransferFrom(String(data.transfer_from_medical_center || ''));
        setTransferTo(String(data.nearest_medical_center || ''));
      } catch (err) {
        console.error('Failed to load case:', err);
        alert('Could not load case details.');
        onHide(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCaseData();
  }, [show, caseId]);

  // Fetch medical centers
  useEffect(() => {
    if (!show) return;

    const fetchMedicalCenters = async () => {
      try {
        const res = await axiosInstance.get('/ems/medical-centers');
        const centers = Array.isArray(res.data) ? res.data : [];
        setMedicalCenters(centers);
      } catch (err) {
        console.error('Failed to load medical centers', err);
      }
    };
    fetchMedicalCenters();
  }, [show]);

  // Update filtered Transfer To
  useEffect(() => {
    setFilteredTransferTo(medicalCenters.filter(c => c.id !== parseInt(transferFrom || 0)));
  }, [transferFrom, medicalCenters]);

  // Fetch doctors when Transfer From changes
  useEffect(() => {
    if (!transferFrom) {
      setDoctors([]);
      return;
    }
    const fetchDoctors = async () => {
      try {
        const res = await axiosInstance.get(`/ems/get-specefic-medical-center-doctors-list/${transferFrom}`);
        const list = Array.isArray(res.data) ? res.data : [];
        setDoctors(list.map(d => ({
          id: d.id,
          name: `${d.first_name} ${d.last_name}`.trim()
        })));
      } catch (err) {
        setDoctors([]);
      }
    };
    fetchDoctors();
  }, [transferFrom]);

  // QR Scanner
  useEffect(() => {
    if (!showScanner) return;
    const scanner = new Html5QrcodeScanner('reader', { fps: 10, qrbox: 250 }, false);
    scanner.render(
      (text) => {
        setFormData(prev => ({ ...prev, bracelet_id: text }));
        setShowScanner(false);
        scanner.clear();
      },
      () => {}
    );
    return () => scanner.clear();
  }, [showScanner]);

  // Handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'name') {
      if (/^[a-zA-Z\s]*$/.test(value)) {
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, name: '' }));
      } else {
        setErrors(prev => ({ ...prev, name: 'Name can only contain letters and spaces' }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleEscortTeamChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      escort_team: { ...prev.escort_team, [name]: value }
    }));
  };

  const handleDoctorChange = (e) => {
    const id = e.target.value;
    const doctor = doctors.find(d => d.id === parseInt(id));
    setFormData(prev => ({
      ...prev,
      doctor: id,
      doctor_name: doctor ? doctor.name : ''
    }));
  };

  const handleTransferFromChange = (e) => {
    const value = e.target.value;
    setTransferFrom(value);
    setFormData(prev => ({ ...prev, doctor: '', doctor_name: '' }));
  };

  const handleTransferToChange = (e) => setTransferTo(e.target.value);

  const handleFileChange = (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jfif', 'application/pdf'];
    const maxSize = 5 * 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, [`documents${index + 1}`]: 'Only PDF, JPG, PNG, WEBP allowed' }));
      return;
    }
    if (file.size > maxSize) {
      setErrors(prev => ({ ...prev, [`documents${index + 1}`]: 'File must be under 5MB' }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      documents: { ...prev.documents, [`documents${index + 1}`]: file }
    }));
  };

  const addFileInput = () => fileInputs.length < MAX_FILES && setFileInputs([...fileInputs, fileInputs.length]);
  const removeFileInput = (index) => {
    const key = `documents${index + 1}`;
    setFileInputs(fileInputs.filter((_, i) => i !== index));
    setFormData(prev => {
      const { [key]: _, ...rest } = prev.documents;
      return { ...prev, documents: rest };
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!transferFrom) newErrors.transferFrom = 'Transfer From is required';
    if (!transferTo) newErrors.transferTo = 'Transfer To is required';
    if (!formData.diagnose.trim()) newErrors.diagnose = 'Diagnose is required';
    if (!formData.doctor) newErrors.doctor = 'Please select a doctor';
    if (!formData.clinic_no.trim()) newErrors.clinic_no = 'Clinic No is required';
    if (!formData.request_details.trim()) newErrors.request_details = 'Request details are required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const documentsBase64 = {};
      for (let i = 1; i <= MAX_FILES; i++) {
        const key = `documents${i}`;
        const file = formData.documents[key];
        documentsBase64[key] = file instanceof File ? await fileToBase64(file) : "";
      }

      const payload = {
        case_name: formData.name,
        age: formData.age ? parseInt(formData.age) : null,
        gender: formData.gender || null,
        nearest_medical_center: parseInt(transferTo),
        transfer_from_medical_center: parseInt(transferFrom),
        diagnose: formData.diagnose,
        bracelet_id: formData.bracelet_id,
        doctor: formData.doctor ? parseInt(formData.doctor) : null,
        doctor_name: formData.doctor_name || null,
        clinic_no: formData.clinic_no,
        request_details: formData.request_details,
        escort_team: formData.escort_team,
        ...documentsBase64,
        _method: 'PUT'
      };

      await axiosInstance.post(`/ems/tranfer/cases/${formData.id}`, payload, {
        headers: { 'Content-Type': 'application/json' }
      });

      alert('Transfer case updated successfully!');
      refreshList();
      onHide(true);
    } catch (err) {
      alert(err.response?.data?.message || 'Update failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (!show) return null;

  return (
    <Modal show={show} onHide={() => onHide(false)} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isLoading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="row">
              {/* Name */}
              <div className="col-md-6 mb-3">
                <label className="form-label">Name <span className="text-danger">*</span></label>
                <input type="text" className={`form-control ${errors.name ? 'is-invalid' : ''}`} name="name" value={formData.name} onChange={handleChange} />
                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Age</label>
                <input type="number" className="form-control" name="age" value={formData.age} onChange={handleChange} min="0" max="120" />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Gender</label>
                <select className="form-control form-select" name="gender" value={formData.gender} onChange={handleChange}>
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Transfer From <span className="text-danger">*</span></label>
                <select className={`form-control form-select ${errors.transferFrom ? 'is-invalid' : ''}`} value={transferFrom} onChange={handleTransferFromChange}>
                  <option value="">Select Medical Center</option>
                  {medicalCenters.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {errors.transferFrom && <div className="invalid-feedback">{errors.transferFrom}</div>}
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Transfer To <span className="text-danger">*</span></label>
                <select className={`form-control form-select ${errors.transferTo ? 'is-invalid' : ''}`} value={transferTo} onChange={handleTransferToChange}>
                  <option value="">Select Medical Center</option>
                  {filteredTransferTo.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {errors.transferTo && <div className="invalid-feedback">{errors.transferTo}</div>}
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Diagnose <span className="text-danger">*</span></label>
                <input type="text" className={`form-control ${errors.diagnose ? 'is-invalid' : ''}`} name="diagnose" value={formData.diagnose} onChange={handleChange} />
                {errors.diagnose && <div className="invalid-feedback">{errors.diagnose}</div>}
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Doctor <span className="text-danger">*</span></label>
                <select className={`form-control form-select ${errors.doctor ? 'is-invalid' : ''}`} value={formData.doctor} onChange={handleDoctorChange}>
                  <option value="">Select Doctor</option>
                  {doctors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
                {errors.doctor && <div className="invalid-feedback">{errors.doctor}</div>}
              </div>

              {/* Bracelet ID */}
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Bracelet ID <span className="text-danger">*</span>
                </label>
                <div className="d-flex align-items-center">
                  <input
                    type="text"
                    className={`form-control ${errors.bracelet_id ? 'is-invalid' : ''}`}
                    name="bracelet_id"
                    value={formData.bracelet_id}
                    onChange={handleChange}
                  />
                  <i
                    className="ti ti-scan text-primary fs-4 ms-3 d-lg-none d-block"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setShowScanner(true)}
                  ></i>
                </div>
                {errors.bracelet_id && <div className="invalid-feedback">{errors.bracelet_id}</div>}
              </div>

              <div className="col-md-12 mb-3">
                <label className="form-label">Clinic No <span className="text-danger">*</span></label>
                <input type="text" className={`form-control ${errors.clinic_no ? 'is-invalid' : ''}`} name="clinic_no" value={formData.clinic_no} onChange={handleChange} />
                {errors.clinic_no && <div className="invalid-feedback">{errors.clinic_no}</div>}
              </div>

              <div className="col-md-12 mb-3">
                <label className="form-label">Request Details <span className="text-danger">*</span></label>
                <textarea className={`form-control ${errors.request_details ? 'is-invalid' : ''}`} name="request_details" value={formData.request_details} onChange={handleChange} rows="3" />
                {errors.request_details && <div className="invalid-feedback">{errors.request_details}</div>}
              </div>

              <div className="col-md-12 mb-3">
                <label className="form-label">Escort Team</label>
                <div className="row">
                  {['escort_physician', 'escort_nurse', 'escort_paramedic', 'other'].map((field) => (
                    <div className="col-md-6 mb-2" key={field}>
                      <input
                        type="text"
                        className="form-control"
                        name={field}
                        value={formData.escort_team[field]}
                        onChange={handleEscortTeamChange}
                        placeholder={field.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Document Uploads */}
                    <div className="row">
                      <div className="col-md-12">
                        <label>Documents Upload</label>
                        <p className="text-muted small mb-2">
                          Allowed file types: PDF, JPG, JPEG, PNG, WEBP, JFIF. Maximum 3 files.
                        </p>
                        {fileInputs.map((_, index) => (
                          <div key={index} className="mb-3 d-flex align-items-center flex-nowrap">
                            <div className="flex-grow-1 me-2">
                              <input
                                className={`form-control ${errors[`documents${index + 1}`] ? 'is-invalid' : ''}`}
                                type="file"
                                onChange={(e) => handleFileChange(e, index)}
                                accept="image/jpeg,image/jpg,image/png,image/webp,image/jfif,application/pdf"
                              />
                              {errors[`documents${index + 1}`] && (
                                <div className="invalid-feedback">{errors[`documents${index + 1}`]}</div>
                              )}
                            </div>
                            <div className="d-flex align-items-center">
                              {index === fileInputs.length - 1 && fileInputs.length < MAX_FILES && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-icon fs-5 me-2"
                                  onClick={addFileInput}
                                >
                                  <i className="ti ti-circle-plus text-success"></i>
                                </button>
                              )}
                              {fileInputs.length > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-sm fs-5 btn-icon"
                                  onClick={() => removeFileInput(index)}
                                >
                                  <i className="ti ti-trash text-danger"></i>
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
            </div>

            <Modal.Footer>
                      <Button variant="outline-danger" onClick={() => onHide(false)} disabled={isLoading}>
                        Cancel
                      </Button>
                      <Button variant="outline-primary" onClick={handleSubmit} disabled={isLoading}>
                        {isLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Updating...
                          </>
                        ) : (
                          'Update'
                        )}
                      </Button>
                    </Modal.Footer>
          </form>
        )}
      </Modal.Body>

      {showScanner && (
        <Modal show={showScanner} onHide={() => setShowScanner(false)}>
          <Modal.Header closeButton><Modal.Title>Scan QR</Modal.Title></Modal.Header>
          <Modal.Body><div id="reader"></div></Modal.Body>
        </Modal>
      )}
    </Modal>
  );
};

export default EditTransferCaseModal;